import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GooglePhotosFeed
 * Pulls photos from the configured Google Business Profile and renders a
 * cinematic masonry-style gallery. Auto-hides itself if the integration
 * is disabled (no API key / Place ID set in backend env).
 */
const GooglePhotosFeed = () => {
  const [photos, setPhotos] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/google-photos/list?limit=12`);
        if (!mounted) return;
        setEnabled(!!data.enabled);
        setPhotos(data.photos || []);
      } catch (e) {
        // Silent — section just hides
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Hide section entirely if integration not configured
  if (!loading && !enabled) return null;
  if (!loading && enabled && photos.length === 0) return null;

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);

  return (
    <section
      id="google-feed"
      className="relative py-24 md:py-32 bg-[#0E0E0E] border-t border-white/5 overflow-hidden"
      data-testid="google-feed-section"
    >
      {/* Ambient backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60vw] h-[40vw] bg-[#4ECDC4]/8 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#4ECDC4]">
              Direto do Nosso Google
            </span>
            <div className="w-8 h-[1px] bg-[#4ECDC4]" />
          </div>
          <h2 className="text-3xl md:text-5xl tracking-tight font-bold text-[#F5F5F0] mb-4">
            Bastidores & <span className="text-[#4ECDC4]">Coberturas Recentes</span>
          </h2>
          <p className="text-base text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed">
            Fotos atualizadas em tempo real diretamente do nosso perfil verificado no Google. Cada imagem é um trabalho real da Coutin Agência em Foz do Iguaçu.
          </p>
          <div className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.25em] text-[#A1A1AA] font-light">
            <Star className="w-3.5 h-3.5 text-[#4ECDC4] fill-[#4ECDC4]" />
            <span className="text-[#4ECDC4] font-bold">5,0</span>
            <span className="text-white/30">·</span>
            <a
              href="https://g.page/r/CV71PwTZvBxPEBM"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#4ECDC4] transition-colors underline decoration-[#4ECDC4]/40 underline-offset-4"
              data-testid="google-feed-link"
            >
              Ver no Google Maps
            </a>
          </div>
        </motion.div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl bg-gradient-to-br from-[#161616] to-[#0E0E0E] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {photos.map((photo, idx) => (
              <motion.button
                key={idx}
                type="button"
                onClick={() => openLightbox(idx)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.4) }}
                whileHover={{ scale: 1.03 }}
                className={`group relative overflow-hidden rounded-xl border border-white/5 hover:border-[#4ECDC4]/50 transition-all duration-300 ${
                  // Vary aspect for visual interest
                  idx % 7 === 0
                    ? 'aspect-[3/4] md:row-span-2'
                    : idx % 5 === 0
                    ? 'aspect-square md:aspect-[4/3]'
                    : 'aspect-square'
                }`}
                data-testid={`google-photo-${idx}`}
              >
                <img
                  src={`${BACKEND_URL}${photo.url}`}
                  alt={`Coutin Agência - Trabalho ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/90 font-bold">
                    <Camera className="w-3 h-3" />
                    <span>Ver foto</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={closeLightbox}
          data-testid="google-photo-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-white text-3xl hover:text-[#4ECDC4] transition-colors"
            aria-label="Fechar"
            data-testid="google-photo-lightbox-close"
          >
            ✕
          </button>
          <img
            src={`${BACKEND_URL}${photos[lightboxIndex].url}?h=1400`}
            alt={`Coutin Agência - ${lightboxIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default GooglePhotosFeed;
